/**
 * Dashboard do Commigo Care Diagnóstico (HNS-1573).
 *
 * Entra na Home pelo motor de dashboards do programa: a chave
 * `commigo_care_dashboard` em `configuracoes.graficos_dashboard` traz este bloco.
 *
 * Le o endpoint /dashboards/commigo_care/, que agrega as mesmas views SQL dos
 * relatorios do programa (commigo_care_*) - por isso os numeros do dashboard sempre
 * batem com os relatorios exportados. Responde as perguntas da operacao e da
 * industria: quantos exames foram autorizados (com/sem voucher) ou negados, quais
 * resultados sairam, onde os exames acontecem e como esta o consumo de vouchers.
 *
 * E um bloco unico (nao um por grafico) porque todos os recortes vem da mesma
 * consulta - registrar varias chaves faria requisicoes identicas.
 */
import React, { Component } from "react";
import { Card, Row, Col, Spinner, Alert, Form, Button, Badge, Table } from "react-bootstrap";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { FaFilter, FaEraser } from "react-icons/fa";
import api from "../../../services/api";
import { getCurrentUser, tiposAcesso } from "../../../common/utils";

//Quem acompanha o programa: operacao (navegadora), gestao interna, a industria e o admin.
//Prestador/laboratorio nao entram - veem apenas os seus atendimentos.
const podeVerDashboard = () => {
    const usuario = getCurrentUser();
    if (usuario['is_superuser']) return true;

    const tipo = usuario['tipo_usuario'];
    if (tipo === '' || tipo === 'operador') return true;   // admin e navegadora/operadora

    if (tipo === 'gestor') {
        const acessos = tiposAcesso() || {};
        const tipoGestor = acessos.gestor?.tipo_gestor;
        return tipoGestor === 'interno' || tipoGestor === 'industria';
    }

    return false;
};

const COR_PRINCIPAL = '#00ADD8';
const COR_APOIO = '#333159';
const COR_POSITIVO = '#1e7e34';
const COR_ALERTA = '#a41c12';
const COR_ATENCAO = '#c78500';
const CINZA = '#6b7a8d';

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const formataMes = (chave) => {
    if (!chave || !/^\d{4}-\d{2}$/.test(chave)) return chave || '-';
    const [ano, mes] = chave.split('-');
    return `${MESES_CURTOS[Number(mes) - 1]}/${ano.slice(2)}`;
};

class CommigoCareDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dados: null,
            carregando: true,
            erro: '',
            data_de: '',
            data_ate: '',
        };
    }

    componentDidMount() {
        this.carregar();
    }

    getProgramaId = () => {
        const usuario = getCurrentUser();
        return this.props.programaId || usuario['ambiente_programa_id'] || null;
    };

    carregar = async () => {
        const programa_id = this.getProgramaId();

        if (!programa_id) {
            this.setState({ carregando: false, erro: 'Selecione um programa para ver os indicadores.' });
            return;
        }

        this.setState({ carregando: true, erro: '' });

        try {
            const params = { programa_id };
            if (this.state.data_de) params.data_de = this.state.data_de;
            if (this.state.data_ate) params.data_ate = this.state.data_ate;

            const { data } = await api.get('/dashboards/commigo_care/', { params });
            this.setState({ dados: data, carregando: false });
        } catch (e) {
            console.error('Erro ao carregar o dashboard do Commigo Care:', e);
            this.setState({
                carregando: false,
                erro: e?.response?.data?.error || 'Não foi possível carregar os indicadores.',
            });
        }
    };

    limpar = () => this.setState({ data_de: '', data_ate: '' }, this.carregar);

    renderIndicador = (titulo, valor, complemento, cor = COR_APOIO) => (
        <Col xs={6} md={4} xl={2} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                    <div style={{ fontSize: '11px', color: CINZA, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                        {titulo}
                    </div>
                    <div style={{
                        fontSize: String(valor).length > 9 ? '19px' : '26px',
                        fontWeight: 700, color: cor, lineHeight: 1.25, whiteSpace: 'nowrap'
                    }}>{valor}</div>
                    {complemento && <div style={{ fontSize: '11px', color: CINZA }}>{complemento}</div>}
                </Card.Body>
            </Card>
        </Col>
    );

    renderCard = (titulo, subtitulo, conteudo, altura = 320, largura = 6) => (
        <Col lg={largura} className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pb-0">
                    <div className="fw-bold">{titulo}</div>
                    {subtitulo && <small className="text-muted">{subtitulo}</small>}
                </Card.Header>
                <Card.Body style={{ height: `${altura}px` }}>{conteudo}</Card.Body>
            </Card>
        </Col>
    );

    semDados = (mensagem = 'Sem registros no período') => (
        <div className="d-flex align-items-center justify-content-center h-100">
            <span className="text-muted" style={{ fontSize: '13px' }}>{mensagem}</span>
        </div>
    );

    badgeSituacaoVoucher = (situacao) => {
        const cores = {
            'Disponível': 'success',
            'Esgotado': 'secondary',
            'Expirado': 'warning',
            'Inativo': 'danger',
        };
        return <Badge bg={cores[situacao] || 'light'} text={cores[situacao] ? undefined : 'dark'}>{situacao || '-'}</Badge>;
    };

    render() {
        const { dados, carregando, erro, data_de, data_ate } = this.state;

        if (!podeVerDashboard()) return null;
        if (carregando) return <div className="text-center py-5"><Spinner animation="border" /></div>;
        if (erro) return <Alert variant="warning" className="m-4">{erro}</Alert>;

        const resumo = dados?.resumo || {};
        const autorizacao = (dados?.exames_por_autorizacao || []).filter(r => r.value > 0);
        const resultados = (dados?.resultados || []).filter(r => r.value > 0);
        const evolucao = dados?.evolucao_mensal || [];
        const localidades = dados?.localidades || [];
        const instituicoes = dados?.instituicoes || [];
        const genero = (dados?.epidemiologia?.genero || []).filter(g => g.value > 0);
        const faixaEtaria = dados?.epidemiologia?.faixa_etaria || [];
        const equipe = dados?.equipe || [];
        const vouchers = dados?.vouchers || [];

        return (
            <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold m-0">Commigo Care Diagnóstico — indicadores do programa</h5>
                        <small className="text-muted">Mesma base dos relatórios: autorização por voucher, resultados e perfil dos exames</small>
                    </div>
                </div>

                {/* filtros */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-3">
                        <Row className="align-items-end g-2">
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Data de</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_de}
                                    onChange={(e) => this.setState({ data_de: e.target.value })}
                                />
                            </Col>
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Data até</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_ate}
                                    onChange={(e) => this.setState({ data_ate: e.target.value })}
                                />
                            </Col>
                            <Col sm={12} lg={6} className="d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={this.carregar}>
                                    <FaFilter className="me-1" /> Filtrar
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={this.limpar}>
                                    <FaEraser className="me-1" /> Limpar
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* indicadores */}
                <Row>
                    {this.renderIndicador('Com voucher', resumo.exames_com_voucher ?? 0, 'exames autorizados', COR_POSITIVO)}
                    {this.renderIndicador('Sem voucher', resumo.exames_sem_voucher ?? 0, 'autorizados pelo cliente', COR_PRINCIPAL)}
                    {this.renderIndicador('Negados', resumo.exames_negados ?? 0, 'no período', COR_ALERTA)}
                    {this.renderIndicador('Pendentes', resumo.exames_pendentes ?? 0, 'aguardando decisão')}
                    {this.renderIndicador('Com resultado', resumo.exames_com_resultado ?? 0, 'laudos concluídos')}
                    {this.renderIndicador('Recoletas', resumo.recoletas ?? 0, 'exames recoletados', COR_ATENCAO)}
                </Row>

                <Row>
                    {/* autorizacao dos exames */}
                    {this.renderCard(
                        'Autorização dos exames',
                        'Autorizados com voucher, sem voucher (aprovação do cliente), negados e pendentes',
                        autorizacao.length ? (
                            <ResponsivePie
                                data={autorizacao}
                                margin={{ top: 16, right: 16, bottom: 56, left: 16 }}
                                innerRadius={0.6}
                                padAngle={0.8}
                                cornerRadius={3}
                                colors={{ datum: 'data.color' }}
                                enableArcLinkLabels={false}
                                arcLabelsTextColor="#fff"
                                legends={[{
                                    anchor: 'bottom', direction: 'row', translateY: 44,
                                    itemWidth: 118, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados(),
                        340, 5
                    )}

                    {/* evolucao mensal por resultado */}
                    {this.renderCard(
                        'Resultados por mês',
                        'Conclusões de laudo por competência, separadas pelo resultado do exame',
                        evolucao.length ? (
                            <ResponsiveBar
                                data={evolucao.map(m => ({
                                    mes: formataMes(m.mes),
                                    Positivo: m.Positivo,
                                    Negativo: m.Negativo,
                                    Inconclusivo: m.Inconclusivo,
                                    'Não disponibilizado': m['Não disponibilizado'] || 0,
                                }))}
                                keys={['Positivo', 'Negativo', 'Inconclusivo', 'Não disponibilizado']}
                                indexBy="mes"
                                groupMode="stacked"
                                margin={{ top: 10, right: 20, bottom: 60, left: 45 }}
                                padding={0.3}
                                colors={[COR_POSITIVO, COR_ALERTA, COR_ATENCAO, CINZA]}
                                labelSkipHeight={14}
                                labelTextColor="#fff"
                                axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: -35 }}
                                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                legends={[{
                                    dataFrom: 'keys', anchor: 'bottom', direction: 'row', translateY: 54,
                                    itemWidth: 125, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados(),
                        340, 7
                    )}
                </Row>

                <Row>
                    {/* resultado dos exames */}
                    {this.renderCard(
                        'Resultado dos exames',
                        'Distribuição dos laudos concluídos no período',
                        resultados.length ? (
                            <ResponsivePie
                                data={resultados}
                                margin={{ top: 16, right: 16, bottom: 56, left: 16 }}
                                innerRadius={0.6}
                                padAngle={0.8}
                                cornerRadius={3}
                                colors={{ datum: 'data.color' }}
                                enableArcLinkLabels={false}
                                arcLabelsTextColor="#fff"
                                legends={[{
                                    anchor: 'bottom', direction: 'row', translateY: 44,
                                    itemWidth: 100, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados(),
                        320, 4
                    )}

                    {/* genero */}
                    {this.renderCard(
                        'Pacientes por gênero',
                        'Perfil epidemiológico — dados agregados e anonimizados',
                        genero.length ? (
                            <ResponsivePie
                                data={genero}
                                margin={{ top: 16, right: 16, bottom: 56, left: 16 }}
                                innerRadius={0.6}
                                padAngle={0.8}
                                cornerRadius={3}
                                colors={[COR_APOIO, COR_PRINCIPAL, '#c9d2de', COR_ATENCAO]}
                                enableArcLinkLabels={false}
                                arcLabelsTextColor="#fff"
                                legends={[{
                                    anchor: 'bottom', direction: 'row', translateY: 44,
                                    itemWidth: 105, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados(),
                        320, 4
                    )}

                    {/* faixa etaria */}
                    {this.renderCard(
                        'Pacientes por faixa etária',
                        'Perfil epidemiológico — dados agregados e anonimizados',
                        faixaEtaria.length ? (
                            <ResponsiveBar
                                data={faixaEtaria.map(f => ({ faixa: f.faixa, Pacientes: f.pacientes }))}
                                keys={['Pacientes']}
                                indexBy="faixa"
                                margin={{ top: 10, right: 20, bottom: 70, left: 40 }}
                                padding={0.3}
                                colors={[COR_PRINCIPAL]}
                                labelSkipHeight={14}
                                labelTextColor="#fff"
                                axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: -35 }}
                                axisLeft={{ tickSize: 0, tickPadding: 8, format: v => Math.floor(v) === v ? v : '' }}
                                enableGridY
                            />
                        ) : this.semDados(),
                        320, 4
                    )}
                </Row>

                <Row>
                    {/* localidade dos exames */}
                    <Col lg={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pb-0">
                                <div className="fw-bold">Localidade dos exames</div>
                                <small className="text-muted">Onde os exames são realizados, por laboratório</small>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '340px', overflowY: 'auto' }}>
                                {localidades.length ? (
                                    <Table hover responsive size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>UF</th>
                                                <th>Cidade</th>
                                                <th>Laboratório</th>
                                                <th className="text-center">Exames</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {localidades.map((l, i) => (
                                                <tr key={`${l.uf}-${l.cidade}-${l.laboratorio}-${i}`}>
                                                    <td>{l.uf}</td>
                                                    <td>{l.cidade}</td>
                                                    <td>{l.laboratorio}</td>
                                                    <td className="text-center">{l.exames}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : this.semDados()}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* instituicoes dos medicos */}
                    <Col lg={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pb-0">
                                <div className="fw-bold">Instituição dos médicos solicitantes</div>
                                <small className="text-muted">Onde atuam os médicos que solicitaram exames</small>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '340px', overflowY: 'auto' }}>
                                {instituicoes.length ? (
                                    <Table hover responsive size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>Instituição</th>
                                                <th>UF</th>
                                                <th>Cidade</th>
                                                <th className="text-center">Médicos</th>
                                                <th className="text-center">Exames</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {instituicoes.map((inst, i) => (
                                                <tr key={`${inst.instituicao}-${i}`}>
                                                    <td>{inst.instituicao}</td>
                                                    <td>{inst.uf}</td>
                                                    <td>{inst.cidade}</td>
                                                    <td className="text-center">{inst.medicos}</td>
                                                    <td className="text-center">{inst.exames}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : this.semDados()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    {/* vouchers */}
                    <Col lg={7} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pb-0">
                                <div className="fw-bold">Utilização de vouchers</div>
                                <small className="text-muted">
                                    Situação atual da base de vouchers (não filtra por período) —
                                    {' '}{resumo.vouchers_disponiveis ?? 0} disponíveis, {resumo.vouchers_utilizacoes ?? 0} utilizações
                                </small>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '340px', overflowY: 'auto' }}>
                                {vouchers.length ? (
                                    <Table hover responsive size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>Voucher</th>
                                                <th className="text-center">Situação</th>
                                                <th className="text-center">Usos</th>
                                                <th className="text-center">Saldo</th>
                                                <th className="text-center">Exames</th>
                                                <th className="text-center">Laudos</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {vouchers.map((v) => (
                                                <tr key={v.codigo}>
                                                    <td>{v.codigo}</td>
                                                    <td className="text-center">{this.badgeSituacaoVoucher(v.situacao)}</td>
                                                    <td className="text-center">{v.usos}{v.limite_uso ? `/${v.limite_uso}` : ''}</td>
                                                    <td className="text-center">{v.saldo === null || v.saldo === undefined ? 'Ilimitado' : v.saldo}</td>
                                                    <td className="text-center">{v.exames_realizados}</td>
                                                    <td className="text-center">{v.laudos_entregues}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : this.semDados('Nenhum voucher cadastrado')}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* equipe especializada */}
                    <Col lg={5} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pb-0">
                                <div className="fw-bold">Atendimento da equipe especializada</div>
                                <small className="text-muted">Registros por competência, público e etapa</small>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '340px', overflowY: 'auto' }}>
                                {equipe.length ? (
                                    <Table hover responsive size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>Competência</th>
                                                <th>Público</th>
                                                <th className="text-center">Registros</th>
                                                <th className="text-center">Atendidos</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {equipe.map((e, i) => (
                                                <tr key={`${e.competencia}-${e.publico}-${e.etapa}-${i}`}>
                                                    <td>{formataMes(e.competencia)}</td>
                                                    <td>{e.publico}{e.etapa ? ` — ${e.etapa}` : ''}</td>
                                                    <td className="text-center">{e.registros}</td>
                                                    <td className="text-center">{e.atendimentos}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : this.semDados()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default CommigoCareDashboard;
